import React, { useEffect, useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { logger } from '../lib/logging';

interface BannerAnnouncement {
  id: string;
  message: string;
  priority: 'info' | 'warning' | 'critical';
  start_at: string;
  end_at: string;
  cta_url: string | null;
  cta_text: string | null;
  audience?: 'all' | 'authenticated';
  created_at: string;
}

interface AnnouncementBannerProps {
  className?: string;
}

const STORAGE_KEY = 'dismissed_banners';

export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  className = '',
}) => {
  const [banner, setBanner] = useState<BannerAnnouncement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Get dismissed banner IDs from localStorage
  const getDismissedBanners = useCallback((): Set<string> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  }, []);

  // Save dismissed banner ID to localStorage
  const saveDismissedBanner = useCallback((bannerId: string) => {
    try {
      const dismissed = getDismissedBanners();
      dismissed.add(bannerId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(dismissed)));
    } catch (error) {
      logger.error('banner.save_dismissal_error', String(error));
    }
  }, [getDismissedBanners]);

  // Fetch active banner
  const fetchActiveBanner = useCallback(async () => {
    try {
      setLoading(true);

      // Get auth token if available
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('/api/banners/active', { headers });

      if (!response.ok) {
        throw new Error('Failed to fetch active banner');
      }

      const data = await response.json();

      if (data.banner) {
        const dismissedBanners = getDismissedBanners();

        // Check if this banner has been dismissed
        if (!dismissedBanners.has(data.banner.id)) {
          setBanner(data.banner);
          logger.info('banner.loaded', undefined, {
            bannerId: data.banner.id,
            priority: data.banner.priority,
          });
        } else {
          logger.info('banner.already_dismissed', undefined, {
            bannerId: data.banner.id,
          });
        }
      }
    } catch (error) {
      logger.error('banner.fetch_error', String(error));
    } finally {
      setLoading(false);
    }
  }, [getDismissedBanners]);

  // Initial fetch
  useEffect(() => {
    fetchActiveBanner();
  }, [fetchActiveBanner]);

  // Handle dismissal
  const handleDismiss = useCallback(async () => {
    if (!banner) return;

    try {
      logger.info('banner.dismiss_start', undefined, { bannerId: banner.id });

      // Save to localStorage immediately
      saveDismissedBanner(banner.id);
      setDismissed(true);

      // Try to save to backend if logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`/api/banners/${banner.id}/dismiss`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            logger.info('banner.dismiss_success_db', undefined, {
              bannerId: banner.id,
            });
          }
        } catch (error) {
          // Silently fail - localStorage dismissal is already saved
          logger.warn('banner.dismiss_db_failed', String(error), {
            bannerId: banner.id,
          });
        }
      }

      logger.info('banner.dismiss_success', undefined, { bannerId: banner.id });
    } catch (error) {
      logger.error('banner.dismiss_error', String(error), {
        bannerId: banner.id,
      });
    }
  }, [banner, saveDismissedBanner]);

  // Don't render if loading, no banner, or dismissed
  if (loading || !banner || dismissed) {
    return null;
  }

  const getPriorityStyles = () => {
    switch (banner.priority) {
      case 'critical':
        return {
          bg: 'bg-red-900/30',
          border: 'border-red-500/50',
          text: 'text-red-200',
          button: 'hover:bg-red-800/40',
          ctaButton:
            'bg-red-500/20 hover:bg-red-500/30 border-red-500/40 text-red-100',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-900/30',
          border: 'border-yellow-500/50',
          text: 'text-yellow-200',
          button: 'hover:bg-yellow-800/40',
          ctaButton:
            'bg-yellow-500/20 hover:bg-yellow-500/30 border-yellow-500/40 text-yellow-100',
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-900/30',
          border: 'border-blue-500/50',
          text: 'text-blue-200',
          button: 'hover:bg-blue-800/40',
          ctaButton:
            'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/40 text-blue-100',
        };
    }
  };

  const styles = getPriorityStyles();

  return (
    <div
      className={`${styles.bg} border ${styles.border} ${styles.text} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <div
              className="text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: banner.message }}
            />
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {banner.cta_url && banner.cta_text && (
              <a
                href={banner.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-4 py-2 ${styles.ctaButton} rounded-lg text-sm font-medium border transition-colors whitespace-nowrap`}
                onClick={() => {
                  logger.info('banner.cta_clicked', undefined, {
                    bannerId: banner.id,
                    ctaUrl: banner.cta_url,
                  });
                }}
              >
                {banner.cta_text}
              </a>
            )}

            <button
              onClick={handleDismiss}
              className={`p-1.5 rounded-lg ${styles.button} transition-colors`}
              aria-label="Dismiss announcement"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
